import React, { useState, useEffect } from 'react'

import { TextInput, Button, Search as SearchIcon } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import {
  string,
  bool,
  object,
} from 'prop-types'

import i18n from 'Pods/Shared/I18nHome'
import ImageSearch from 'Pods/Shared/ImageSearch/ImageSearch'

import AiModeButton from './AiModeButton'
import Suggestions from './Suggestions'
import useGroupedLiveSearch from './useGroupedLiveSearch'
import useSearch from './useSearch'

let HomeSearchDropdown

function SearchContent({
  placeholder = '',
  locale = 'en',
  printReady = false,
  aiMode = false,
  searchDropdown = null,
}) {
  const {
    searchRef,
    searchedValue,
    submitting,
    suggestions,
    showSuggestions,
    isFocused,
    actualPlaceholder,
    handleChange,
    handleKeyDown,
    handleFocus,
    handleSearchClick,
    handleKeywordSuggestionClick,
  } = useSearch({ placeholder, locale, printReady, aiMode })

  const [isDropdownLoaded, setIsDropdownLoaded] = useState(false)
  const [isImageSearchActive, setIsImageSearchActive] = useState(false)
  // Keep the AI dropdown open while typing - it consumes the query to build category-scoped
  // search links, so the user never loses their selection mid-keystroke. The image-upload popover
  // shares this anchor and a lower z-index, so it would open hidden behind the dropdown; hide the
  // dropdown while image search is active and restore it (input is still focused) once it closes.
  const showSearchDropdown = aiMode && isFocused && Boolean(searchDropdown) && !isImageSearchActive

  const { results: groupedResults, isLoading: isSearchLoading } = useGroupedLiveSearch({
    query: searchedValue,
    enabled: showSearchDropdown,
  })

  useEffect(() => {
    let isMounted = true

    if (showSearchDropdown && !isDropdownLoaded) {
      if (HomeSearchDropdown) {
        // The module ref persists at module scope; on a remount it can already be loaded while
        // this instance's state reset to false - sync state instead of re-importing.
        setIsDropdownLoaded(true)
      } else {
        import('Pods/LazyLoaded/HomeSearchDropdown/HomeSearchDropdown')
          .then((module) => {
            if (isMounted) {
              HomeSearchDropdown = module.default
              setIsDropdownLoaded(true)
            }
          })
          // eslint-disable-next-line no-console
          .catch((error) => console.error('Failed to load HomeSearchDropdown:', error))
      }
    }

    return () => {
      isMounted = false
    }
  }, [showSearchDropdown, isDropdownLoaded])

  const searchAriaLabel = i18n.t('search.submitAriaLabel', { ns: 'new-home', lng: locale })

  const searchIcon = submitting ? (
    <div className="new-search__loader-wrapper">
      <div className="cgt-spinner cgt-spinner--xsmall new-search__loader" />
    </div>
  ) : (
    <SearchIcon />
  )

  const inputField = (trailButton) => (
    <TextInput
      id="new-search-input"
      isPilot
      placeholder={actualPlaceholder}
      type="primary"
      inputType="text"
      autoComplete="off"
      trailButton={trailButton}
      handleChange={handleChange}
      onKeyDown={handleKeyDown}
      onFocus={handleFocus}
      classNames="new-search__input"
      value={searchedValue}
      data-track="search-input"
      data-content-type="search"
      aria-expanded={aiMode ? showSearchDropdown : undefined}
      aria-controls={showSearchDropdown ? 'home-search-dropdown' : undefined}
    />
  )

  const suggestionsList = showSuggestions && suggestions?.keywords?.length ? (
    <Suggestions
      suggestions={suggestions}
      onKeywordClick={handleKeywordSuggestionClick}
      locale={locale}
    />
  ) : null

  const searchDropdownPanel = showSearchDropdown && isDropdownLoaded && HomeSearchDropdown ? (
    <HomeSearchDropdown
      columns={searchDropdown.columns}
      aiCards={searchDropdown.aiCards}
      aiSectionTitle={searchDropdown.aiSectionTitle}
      ariaLabel={searchDropdown.ariaLabel}
      groupedResults={groupedResults}
      isLoading={isSearchLoading}
      query={searchedValue}
    />
  ) : null

  if (aiMode) {
    return (
      <div ref={searchRef} className="new-search__wrap new-search__wrap--ai">
        <form
          className="new-search new-search--ai"
          onSubmit={(e) => {
            e.preventDefault()
            handleSearchClick()
          }}
          data-track="search-form"
          data-content-type="search"
        >
          {inputField(null)}
          <ImageSearch locale={locale} onActiveChange={setIsImageSearchActive} />
          <div className="new-search__controls">
            <Button
              size="medium"
              icon={searchIcon}
              variant="only-icon"
              actionType="submit"
              customClass="new-search__search-btn"
              aria-label={searchAriaLabel}
              data-track="search-submit"
              data-content-type="search"
            />
            <AiModeButton value={searchedValue} locale={locale} />
          </div>
          {searchDropdownPanel}
          {!showSearchDropdown && suggestionsList}
        </form>
      </div>
    )
  }

  const trailButton = (
    <Button
      size="medium"
      icon={searchIcon}
      variant="only-icon"
      actionType="submit"
      style={{ position: 'absolute', right: '4px', top: '4px' }}
      aria-label={searchAriaLabel}
      data-track="search-submit"
      data-content-type="search"
    />
  )

  return (
    <div ref={searchRef} className="new-search__wrap new-search__wrap--with-image">
      <ImageSearch locale={locale} />
      <form
        className="new-search"
        onSubmit={(e) => {
          e.preventDefault()
          handleSearchClick()
        }}
        data-track="search-form"
        data-content-type="search"
      >
        {inputField(trailButton)}
        {suggestionsList}
      </form>
    </div>
  )
}

SearchContent.propTypes = {
  placeholder: string,
  locale: string,
  printReady: bool,
  aiMode: bool,
  searchDropdown: object,
}

export default SearchContent
